import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { parseISO, isValid, differenceInHours, isBefore, isAfter } from 'date-fns';

// Default timezone - you can make this configurable per store or user
export const DEFAULT_TIMEZONE = 'Europe/London'; // GMT/BST

// Get the user's browser timezone
export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Could not determine browser timezone, falling back to default');
    return DEFAULT_TIMEZONE;
  }
};

// Get the store's timezone (this would come from store settings in real implementation)
export const getStoreTimezone = (): string => {
  // For consistency, always use the default timezone for the store
  // In a real implementation, this would fetch from store settings
  return DEFAULT_TIMEZONE;
};

// Convert a date string to a timezone-aware Date object
export const parseDate = (dateString: string): Date => {
  // Handle null, undefined, or empty strings
  if (!dateString || typeof dateString !== 'string') {
    console.warn('parseDate: Invalid input - empty or not a string');
    return new Date();
  }
  
  // Try parseISO first for ISO 8601 format (which server dates should be)
  let parsed = parseISO(dateString);
  if (isValid(parsed)) {
    return parsed;
  }
  
  // If that fails, try native Date constructor for other formats
  parsed = new Date(dateString);
  if (!isNaN(parsed.getTime())) {
    return parsed;
  }
  
  // If both fail, log warning and return fallback
  console.warn(`parseDate: Could not parse date string "${dateString}"`);
  return new Date();
};

// Format a date for display in a specific timezone
export const formatDateInTimezone = (
  date: Date | string,
  formatString: string,
  timezone: string = DEFAULT_TIMEZONE
): string => {
  try {
    const dateObj = typeof date === 'string' ? parseDate(date) : date;
    if (!isValid(dateObj)) {
      return 'Invalid Date';
    }
    return formatInTimeZone(dateObj, timezone, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

// Get current time in a specific timezone
export const getNowInTimezone = (timezone: string = DEFAULT_TIMEZONE): Date => {
  return utcToZonedTime(new Date(), timezone);
};

// Convert a local time to UTC for server submission
export const convertToUTC = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): Date => {
  try {
    const dateObj = typeof date === 'string' ? parseDate(date) : date;
    return zonedTimeToUtc(dateObj, timezone);
  } catch (error) {
    console.error('Error converting to UTC:', error);
    return new Date();
  }
};

// Convert UTC time to local timezone
export const convertFromUTC = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): Date => {
  try {
    const dateObj = typeof date === 'string' ? parseDate(date) : date;
    return utcToZonedTime(dateObj, timezone);
  } catch (error) {
    console.error('Error converting from UTC:', error);
    return new Date();
  }
};

// Common date formatting functions
export const formatDate = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): string => {
  return formatDateInTimezone(date, 'MMM d, yyyy', timezone);
};

// Format date with day of the week instead of year
export const formatDateWithDay = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): string => {
  return formatDateInTimezone(date, 'EEE, MMM d', timezone);
};

export const formatTime = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): string => {
  return formatDateInTimezone(date, 'h:mm a', timezone);
};

export const formatDateTime = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): string => {
  return formatDateInTimezone(date, 'MMM d, yyyy h:mm a', timezone);
};

export const formatDateTimeShort = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): string => {
  return formatDateInTimezone(date, 'M/d/yy h:mm a', timezone);
};

export const formatDateOnly = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): string => {
  return formatDateInTimezone(date, 'yyyy-MM-dd', timezone);
};

export const formatTimeOnly = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): string => {
  return formatDateInTimezone(date, 'HH:mm', timezone);
};

export const formatTimeRange = (
  startDate: Date | string,
  endDate: Date | string,
  timezone: string = DEFAULT_TIMEZONE
): string => {
  const startTime = formatTime(startDate, timezone);
  const endTime = formatTime(endDate, timezone);
  return `${startTime} - ${endTime}`;
};

// Date comparison functions that are timezone-aware
export const isDateInPast = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): boolean => {
  try {
    const dateObj = typeof date === 'string' ? parseDate(date) : date;
    const dateInTimezone = convertFromUTC(dateObj, timezone);
    const nowInTimezone = getNowInTimezone(timezone);
    return isBefore(dateInTimezone, nowInTimezone);
  } catch (error) {
    console.error('Error checking if date is in past:', error);
    return false;
  }
};

export const isDateInFuture = (date: Date | string, timezone: string = DEFAULT_TIMEZONE): boolean => {
  try {
    const dateObj = typeof date === 'string' ? parseDate(date) : date;
    const dateInTimezone = convertFromUTC(dateObj, timezone);
    const nowInTimezone = getNowInTimezone(timezone);
    return isAfter(dateInTimezone, nowInTimezone);
  } catch (error) {
    console.error('Error checking if date is in future:', error);
    return false;
  }
};

export const isDateOverdue = (
  date: Date | string,
  hoursThreshold: number = 1,
  timezone: string = DEFAULT_TIMEZONE
): boolean => {
  try {
    const dateObj = typeof date === 'string' ? parseDate(date) : date;
    const dateInTimezone = convertFromUTC(dateObj, timezone);
    const nowInTimezone = getNowInTimezone(timezone);
    const hoursDiff = differenceInHours(nowInTimezone, dateInTimezone);
    return hoursDiff > hoursThreshold;
  } catch (error) {
    console.error('Error checking if date is overdue:', error);
    return false;
  }
};

// Helper function to get the same day in a timezone
export const isSameDay = (
  date1: Date | string,
  date2: Date | string,
  timezone: string = DEFAULT_TIMEZONE
): boolean => {
  try {
    const date1Obj = typeof date1 === 'string' ? parseDate(date1) : date1;
    const date2Obj = typeof date2 === 'string' ? parseDate(date2) : date2;
    
    const date1InTimezone = convertFromUTC(date1Obj, timezone);
    const date2InTimezone = convertFromUTC(date2Obj, timezone);
    
    return formatDateInTimezone(date1InTimezone, 'yyyy-MM-dd', timezone) === 
           formatDateInTimezone(date2InTimezone, 'yyyy-MM-dd', timezone);
  } catch (error) {
    console.error('Error checking if dates are same day:', error);
    return false;
  }
};

// Convert datetime-local input value to proper format for server
export const convertDateTimeLocalToUTC = (
  dateTimeLocal: string,
  timezone: string = DEFAULT_TIMEZONE
): string => {
  try {
    if (!dateTimeLocal) return '';
    
    // datetime-local format: "YYYY-MM-DDTHH:MM"
    // Ensure we have seconds in the format
    const fullDateTime = dateTimeLocal.includes(':') && dateTimeLocal.split(':').length === 2 
      ? `${dateTimeLocal}:00` 
      : dateTimeLocal;
    
    // Use zonedTimeToUtc to properly convert from store timezone to UTC
    const utcDate = zonedTimeToUtc(fullDateTime, timezone);
    return utcDate.toISOString();
  } catch (error) {
    console.error('Error converting datetime-local to UTC:', error);
    return '';
  }
};

// Convert server datetime to datetime-local format for input
export const convertUTCToDateTimeLocal = (
  utcDateTime: string,
  timezone: string = DEFAULT_TIMEZONE
): string => {
  try {
    if (!utcDateTime) return '';
    
    const utcDate = parseISO(utcDateTime);
    
    // Use formatInTimeZone for more reliable timezone conversion
    const formatted = formatInTimeZone(utcDate, timezone, 'yyyy-MM-dd\'T\'HH:mm');
    
    return formatted;
  } catch (error) {
    console.error('Error converting UTC to datetime-local:', error);
    return '';
  }
};

// Debug function to log timezone information
export const debugTimezone = (date: Date | string, timezone: string = DEFAULT_TIMEZONE) => {
  const dateObj = typeof date === 'string' ? parseDate(date) : date;
  console.log('Timezone Debug:', {
    originalDate: dateObj,
    timezone,
    inTimezone: convertFromUTC(dateObj, timezone),
    formatted: formatDateTime(dateObj, timezone),
    browserTimezone: getBrowserTimezone(),
  });
};
